import type { ComponentProps } from 'react'
import { useMemo } from 'react'
import type { Validate, ValidationRule } from 'react-hook-form'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'

import type Input from './RHFInput'

import { FLIGHT_CODE_REGEX } from 'constants/flight'

export const TIME_REGEX = /[0-9]:[0-9]/

export const VALID_URL_REGEX =
  /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/

/* RFC 5322 Offical Standard Email Regex */
export const VALID_EMAIL_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const VALID_URL_NO_SCHEME_REGEX =
  /^[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/

export const prependUrlScheme = (url: string) => {
  if (VALID_URL_NO_SCHEME_REGEX.test(url)) {
    return 'https://' + url
  }

  return url
}

function createUrlValidator(invalidUrlMessage: string): Record<string, Validate<any, any>> {
  return {
    noSpace: (url: string | undefined) => {
      return url?.includes(' ') ? invalidUrlMessage : true
    },
    isValidUrl: (url: string | undefined) => {
      if (url) {
        if (VALID_URL_REGEX.test(url)) {
          return true
        }
        return invalidUrlMessage
      }
      return true
    },
  }
}

export const formOptions = {
  mode: 'onBlur',
  shouldFocusError: true,
  reValidateMode: 'onBlur',
} as const

export const flightConfirmationCodeConfig = {}

export const priceRegisterConfig = {
  setValueAs: (v: string | null | undefined) =>
    v === '' || v === null || v === undefined ? undefined : parseFloat(v),
  min: 0,
}

export const priceFieldProps: Partial<ComponentProps<typeof Input>> = {
  type: 'number',
  step: 0.01,
}

function getFieldValidation(intl: IntlShape) {
  const invalidUrlMessage = intl.formatMessage({
    id: 'sR5GZgvzlutLE3Wfwye0',
    defaultMessage: 'Please enter a valid URL',
    description: 'Form validation error for invalid URL input in the trip planner item editor modal',
  })

  return {
    required: {
      message: intl.formatMessage({
        id: 'NcBmD3eui95UOEmuR70e',
        defaultMessage: 'Please fill in this field.',
        description: 'Form validation error for the required field in the trip planner item editor modal',
      }),
      value: true,
    } as ValidationRule<boolean>,

    timePatternValidation: {
      value: TIME_REGEX,
      message: intl.formatMessage({
        id: 'LyoEiSrEvqopDgl6aM8V',
        defaultMessage: 'Please select a time.',
        description: 'Validation error for invalid time selection when an issue occurs',
      }),
    } as ValidationRule<RegExp>,

    emailConfig: {
      required: intl.formatMessage({
        id: 'EgUJlGuhedPxzU-lr7u9',
        defaultMessage: 'Please enter email address.',
        description: 'Validation error for missing email address when an issue occurs',
      }),
      pattern: {
        message: intl.formatMessage({
          id: '5iQltVoJQ_3ruBxYx6Rp',
          defaultMessage: 'Please enter a valid email address.',
          description: 'Validation error for invalid email format when an issue occurs',
        }),
        value: VALID_EMAIL_REGEX,
      },
    },

    flightCarrierCodeConfig: {
      required: {
        value: true,
        message: intl.formatMessage({
          id: 'uY2vKLLSOdKkKHE4Jrz7',
          defaultMessage: 'Please enter a valid flight number',
          description: 'Form validation error for invalid flight carrier code',
        }),
      },
      pattern: {
        value: FLIGHT_CODE_REGEX,
        message: intl.formatMessage({
          id: 'PhVSb1rabj-DVaASs9k8',
          defaultMessage: 'Please enter a valid flight number',
          description: 'Validation error for invalid flight number pattern',
        }),
      },
    },

    urlRegisterConfig: {
      validate: createUrlValidator(invalidUrlMessage),
      setValueAs: prependUrlScheme,
    },

    airportConfig: {
      pattern: {
        message: intl.formatMessage({
          id: 'qP_BdZKFQBlAPWbgb7kB',
          defaultMessage: 'Please select an airport.',
          description: 'Validation error for invalid airport code when an issue occurs',
        }),
        value: /[A-Z]{3}/,
      },
    },
  }
}

export function useFieldValidation() {
  const intl = useIntl()
  return useMemo(() => getFieldValidation(intl), [intl])
}
